// ---------------------------------------------------------------------------
// useAadGroupSearch — Debounced AAD group search via /accesscontrolgroup/aad/search.
//
// Only fires the API call when the debounced query is non-empty.
// Clears results immediately when the query is cleared.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

import type { AadGroupInfo } from '../../services/accessControlService.interface.js';

import type {
  UseAadGroupSearchParams,
  UseAadGroupSearchResult,
} from './useAadGroupSearch.interface.js';

const DEFAULT_DEBOUNCE_MS = 300;

export function useAadGroupSearch({
  query,
  service,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseAadGroupSearchParams): UseAadGroupSearchResult {
  const [groups, setGroups] = useState<AadGroupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length === 0) {
      setGroups([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    let cancelled = false;

    const timeoutId = setTimeout(() => {
      service
        .searchAadGroups(trimmed)
        .then((results) => {
          if (!cancelled) {
            setGroups(results);
            setIsLoading(false);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(
              err instanceof Error ? err.message : 'Failed to search groups',
            );
            setGroups([]);
            setIsLoading(false);
          }
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [query, service, debounceMs]);

  return { groups, isLoading, error };
}
